import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'
import { qty, qtyUnit, signedPercent, productTypeOptions, ProductNameCell, FOOT_EXTRA_STYLE, relativeStats } from './chainStockUi'

// Что показывает ячейка матрицы: средняя цена закупки, количество или сумма
const CELL_MODES = ['price', 'quantity', 'amount']

function ElementIncomes() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('price')
	const [search, setSearch] = useState('')
	const [onlyCompared, setOnlyCompared] = useState(false)
	const [details, setDetails] = useState(null)

	const filters = useChainFilters({ supplier_ids: [], category_ids: [], product_type_ids: [] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['suppliers', 'categories'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/element-incomes', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	function cellValue(row, posId) {
		const cell = row.cells?.[posId]
		if (!cell) return null
		if (cellMode === 'quantity') return cell.quantity
		if (cellMode === 'amount') return cell.amount
		return cell.price
	}

	function totalValue(row) {
		if (cellMode === 'quantity') return row.quantity
		if (cellMode === 'amount') return row.amount
		return row.avg_price
	}

	function formatCell(value) {
		if (value === null || value === undefined) return '—'
		return cellMode === 'quantity' ? qty(value) : money(value)
	}

	const rows = useMemo(() => {
		let list = data?.rows || []
		if (onlyCompared) list = list.filter(row => row.min_price !== null && row.min_price !== row.max_price)
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(row => row.product_name.toLowerCase().includes(query) || (row.category_name || '').toLowerCase().includes(query))
	}, [data, search, onlyCompared])

	const readSortValue = (row, key) => {
		if (key.startsWith('pos:')) return cellValue(row, Number(key.slice(4)))
		if (key === 'total') return totalValue(row)
		return row[key]
	}
	const { sorted, sort, toggle } = useSort(rows, 'amount', 'desc', readSortValue)

	const branchStats = useMemo(() => ({
		overpay_percent: columnStats(branches.filter(item => item.amount > 0), 'overpay_percent'),
	}), [branches])
	const maxBranchAmount = useMemo(() => Math.max(0, ...branches.map(item => item.amount)), [branches])
	const maxOverpay = useMemo(() => Math.max(0, ...(data?.rows || []).filter(row => !row.suspicious).map(row => row.overpay)), [data])
	const topOverpayer = useMemo(() => {
		const list = branches.filter(item => item.overpay > 0)
		return list.length > 0 ? [...list].sort((a, b) => b.overpay - a.overpay)[0] : null
	}, [branches])
	const topBuyer = branches.length > 0 ? [...branches].sort((a, b) => b.amount - a.amount)[0] : null

	async function openDetails(row, posId = null) {
		const response = await GET(buildAnalyticsUrl('/chain/report/element-incomes-documents', applied, {
			product_id: row.product_id,
			pos_id: posId,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				row,
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
			})
		}
	}

	// Цены по поставщикам в расшифровке — кто из поставщиков дешевле
	const supplierRows = useMemo(() => {
		if (!details) return []
		const map = {}
		details.rows.forEach(item => {
			const key = item.supplier_id || 0
			if (!map[key]) map[key] = { supplier_id: key, supplier_name: item.supplier_name, quantity: 0, amount: 0, documents: 0 }
			map[key].quantity += Number(item.quantity)
			map[key].amount += Number(item.amount)
			map[key].documents += 1
		})
		return Object.values(map)
			.map(item => ({ ...item, price: item.quantity > 0 ? item.amount / item.quantity : null }))
			.sort((a, b) => b.amount - a.amount)
	}, [details])
	const supplierStats = useMemo(() => relativeStats(columnStats(supplierRows, 'price')), [supplierRows])
	const documentStats = useMemo(() => relativeStats(columnStats(details?.rows || [], 'price')), [details])

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const all = data.rows || []
		const productColumns = [
			{ label: t('name2'), type: 'text', width: 34 },
			{ label: t('unit'), type: 'text', width: 8 },
			{ label: t('category'), type: 'text', width: 20 },
		]
		const productCells = row => [row.product_name, row.unit_name || '', row.category_name || '']
		const matrix = (name, read, type, extra, extraRow, footer) => ({
			name,
			title: `${t('chain_incomes_title')} — ${name}`,
			subtitle: period,
			columns: [...productColumns, ...branches.map(branch => ({ label: branch.pos_name, type })), ...extra],
			rows: all.map(row => [...productCells(row), ...branches.map(branch => {
				const cell = row.cells?.[branch.pos_id]
				return cell ? read(cell) : ''
			}), ...extraRow(row)]),
			footer,
		})

		downloadExcel(`${t('chain_incomes_title')} ${periodText(data.start_date, data.end_date)}`, [
			matrix(t('chain_incomes_avg_price'), cell => cell.price, 'money', [
				{ label: t('chain_incomes_network_price'), type: 'money' },
				{ label: t('min'), type: 'money' },
				{ label: t('max'), type: 'money' },
				{ label: t('chain_incomes_spread'), type: 'percent', width: 10 },
				{ label: t('chain_incomes_overpay'), type: 'money' },
				{ label: t('chain_incomes_check_units'), type: 'text', width: 14 },
			], row => [row.avg_price, row.min_price, row.max_price, row.spread, row.overpay, row.suspicious ? t('yes') : '']),
			matrix(t('quantity'), cell => cell.quantity, 'number', [{ label: t('total'), type: 'number' }], row => [row.quantity]),
			matrix(t('amount'), cell => cell.amount, 'money', [{ label: t('total'), type: 'money' }], row => [row.amount],
				[t('total'), '', '', ...branches.map(branch => branch.amount), totals.amount]),
			{
				name: t('chain_branches'),
				title: `${t('chain_incomes_title')} — ${t('chain_branches')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('amount'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: t('documents'), type: 'int', width: 11 },
					{ label: t('suppliers'), type: 'int', width: 11 },
					{ label: t('chain_incomes_positions'), type: 'int', width: 11 },
					{ label: t('chain_incomes_overpay'), type: 'money' },
					{ label: t('chain_incomes_overpay_percent'), type: 'percent', width: 12 },
				],
				rows: [...branches].sort((a, b) => b.amount - a.amount).map(branch => [
					branch.pos_name, branch.amount, branch.share, branch.documents, branch.suppliers, branch.positions, branch.overpay, branch.overpay_percent,
				]),
				footer: [t('total'), totals.amount, 100, totals.documents, totals.suppliers, totals.positions, totals.overpay, totals.overpay_percent],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_incomes_title')} — ${details.row.product_name} — ${details.pos_name}`, [{
			name: t('documents'),
			title: `${details.row.product_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('document_number'), type: 'text', width: 14 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('supplier'), type: 'text', width: 28 },
				{ label: t('wms'), type: 'text', width: 18 },
				{ label: t('quantity'), type: 'number' },
				{ label: t('price'), type: 'money' },
				{ label: t('amount'), type: 'money' },
				{ label: t('chain_incomes_document_total'), type: 'money' },
				{ label: t('note'), type: 'text', width: 30 },
			],
			rows: details.rows.map(row => [
				formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), row.document_number || `#${row.id}`, row.pos_name, row.supplier_name,
				row.wms_name || '', row.quantity, row.price, row.amount, row.total_amount, row.note || '',
			]),
			footer: [t('total'), '', '', '', '', details.quantity, details.avg_price, details.amount, '', ''],
		}])
	}

	const modeLabels = {
		price: t('chain_incomes_avg_price'),
		quantity: t('quantity'),
		amount: t('amount'),
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_incomes_title')}
				icon="uil-truck-loading"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<FilterField label={t('suppliers')}>
					<FilterSelect isMulti options={options.suppliers} allLabel={t('chain_all_suppliers')} value={draft.supplier_ids} onChange={(ids) => patch({ supplier_ids: ids })} />
				</FilterField>
				<FilterField label={t('categories')}>
					<FilterSelect isMulti options={options.categories} allLabel={t('chain_all_categories')} value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_product_type')}>
					<FilterSelect isMulti options={productTypeOptions(t)} allLabel={t('chain_all_types')} value={draft.product_type_ids} onChange={(ids) => patch({ product_type_ids: ids })} />
				</FilterField>
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_incomes_total')} icon="uil-truck-loading" color="#5b73e8"
						value={money(totals.amount)}
						sub={<>{formatMoney(totals.documents)} {t('chain_incomes_documents_lower')} · {formatMoney(totals.suppliers)} {t('chain_incomes_suppliers_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_incomes_overpay')} icon="uil-money-withdraw" color="#f06548" title2={t('chain_incomes_overpay_hint')}
						value={money(totals.overpay)}
						sub={<>{percent(totals.overpay_percent, 2)} {t('chain_incomes_of_purchases')} · {formatMoney(totals.compared_positions)} {t('chain_incomes_compared_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_incomes_top_overpayer')} icon="uil-arrow-up-right" color="#f59f00"
						value={topOverpayer ? topOverpayer.pos_name : '—'}
						sub={topOverpayer ? <>{money(topOverpayer.overpay)} · {percent(topOverpayer.overpay_percent, 2)} {t('chain_incomes_of_purchases')}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_incomes_top_buyer')} icon="uil-store" color="#12b886"
						value={topBuyer && topBuyer.amount > 0 ? topBuyer.pos_name : '—'}
						sub={topBuyer && topBuyer.amount > 0 ? <>{money(topBuyer.amount)} · {percent(topBuyer.share)}</> : null} />
				</div>
			</div>

			{totals.suspicious_positions > 0 && (
				<div className="chain-note mb-3">
					<i className="uil uil-exclamation-triangle"></i>
					<span>
						{t('chain_incomes_suspicious_note')}: <b>{formatMoney(totals.suspicious_positions)}</b> · {money(totals.suspicious_overpay)}
					</span>
				</div>
			)}

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_incomes_by_products')}</h5>
							<FilterSegment
								options={CELL_MODES.map(id => ({ id, label: modeLabels[id] }))}
								value={cellMode}
								onChange={setCellMode}
							/>
							<label className="chain-check">
								<input type="checkbox" checked={onlyCompared} onChange={(e) => setOnlyCompared(e.target.checked)} />
								{t('chain_incomes_only_different')}
							</label>
						</div>
						<div className="chain-toolbar-group">
							{cellMode === 'price'
								? <HeatLegend low={t('chain_incomes_expensive')} high={t('chain_incomes_cheap')} />
								: <HeatLegend mode="success" />}
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_product')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="product_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('name2')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey="total" sort={sort} onSort={toggle} className="text-end chain-total-col">
										{cellMode === 'price' ? t('chain_incomes_network_price') : t('total')}
									</SortTh>
									<SortTh sortKey="spread" sort={sort} onSort={toggle} className="text-end" title={t('chain_incomes_spread_hint')}>{t('chain_incomes_spread')}</SortTh>
									<SortTh sortKey="overpay" sort={sort} onSort={toggle} className="text-end" title={t('chain_incomes_overpay_hint')}>{t('chain_incomes_overpay')}</SortTh>
									{cellMode !== 'amount' && (
										<SortTh sortKey="amount" sort={sort} onSort={toggle} className="text-end">{t('amount')}</SortTh>
									)}
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={branches.length + (cellMode !== 'amount' ? 6 : 5)} />}
								{sorted.map((row, rowIndex) => {
									const values = branches.map(branch => cellValue(row, branch.pos_id))
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									const priceStats = relativeStats(columnStats(branches.map(branch => row.cells?.[branch.pos_id]).filter(Boolean), 'price'))
									return (
										<tr key={row.product_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<ProductNameCell row={row} t={t} onClick={() => openDetails(row)} title={t('chain_incomes_open_documents')}>
												{row.suspicious && (
													<i className="uil uil-exclamation-triangle ms-1" style={{ color: '#f59f00' }} title={t('chain_incomes_check_units_hint')}></i>
												)}
											</ProductNameCell>
											{branches.map(branch => {
												const cell = row.cells?.[branch.pos_id]
												const value = cellValue(row, branch.pos_id)
												let style
												if (cell && cellMode === 'price') style = heatStyle(cell.price, priceStats, false)
												else if (cell) style = intensityStyle(value, rowMax, 'success')
												return (
													<td key={branch.pos_id}
														className={`chain-num ${cell ? 'chain-click' : ''}`}
														style={style}
														title={cell ? `${branch.pos_name}: ${qtyUnit(cell.quantity, row.unit_name)} · ${money(cell.amount)} · ${t('price')} ${money(cell.min_price)}–${money(cell.max_price)} · ${formatMoney(cell.documents)} ${t('chain_incomes_documents_lower')}` : undefined}
														onClick={() => cell && openDetails(row, branch.pos_id)}>
														{formatCell(value)}
														{cell && cellMode === 'price' && cell.deviation !== null && Math.abs(cell.deviation) >= 0.05 && (
															<div className="chain-muted">{signedPercent(cell.deviation)}</div>
														)}
														{cell && cellMode !== 'price' && cell.overpay > 0 && !row.suspicious && (
															<div className="chain-muted" title={t('chain_incomes_overpay')}>+{money(cell.overpay)}</div>
														)}
													</td>
												)
											})}
											<td className="chain-num chain-total-col">{formatCell(totalValue(row))}</td>
											<td className="chain-num" title={row.min_price !== null ? `${money(row.min_price)} – ${money(row.max_price)}` : undefined}>
												{row.spread !== null ? percent(row.spread) : '—'}
											</td>
											{row.suspicious
												? <td className="chain-num chain-muted" title={t('chain_incomes_check_units_hint')}>{money(row.overpay)}</td>
												: (
													<FillCell percent={maxOverpay > 0 ? row.overpay / maxOverpay * 100 : 0} tone="danger">
														{row.overpay > 0 ? money(row.overpay) : '—'}
													</FillCell>
												)}
											{cellMode !== 'amount' && <td className="chain-num">{money(row.amount)}</td>}
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tbody style={FOOT_EXTRA_STYLE}>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('documents')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num">
												{formatMoney(branch.documents)}
												<div className="chain-muted">{formatMoney(branch.suppliers)} {t('chain_incomes_suppliers_lower')}</div>
											</td>
										))}
										<td className="chain-num chain-total-col">{formatMoney(totals.documents)}</td>
										<td></td>
										<td></td>
										{cellMode !== 'amount' && <td></td>}
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_incomes_overpay_hint')}>{t('chain_incomes_overpay')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={branch.amount > 0 ? heatStyle(branch.overpay_percent, branchStats.overpay_percent, false) : undefined}>
												{money(branch.overpay)}
												<div className="chain-muted">{percent(branch.overpay_percent, 2)}</div>
											</td>
										))}
										<td className="chain-num chain-total-col">
											{money(totals.overpay)}
											<div className="chain-muted">{percent(totals.overpay_percent, 2)}</div>
										</td>
										<td></td>
										<td></td>
										{cellMode !== 'amount' && <td></td>}
									</tr>
								</tbody>
							)}
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_incomes_total')}</td>
										{branches.map(branch => (
											<FillCell key={branch.pos_id} percent={maxBranchAmount > 0 ? branch.amount / maxBranchAmount * 100 : 0} tone="primary">
												{money(branch.amount)}
												<div className="chain-muted">{percent(branch.share)}</div>
											</FillCell>
										))}
										<td className="chain-num chain-total-col">{money(totals.amount)}</td>
										<td></td>
										<td></td>
										{cellMode !== 'amount' && <td></td>}
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_incomes_matrix_hint')}
					</div>
				</div>
			</div>

			<OverpayChart branches={branches} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.row.product_name} — {details?.pos_name}
						<div className="chain-muted fw-normal">
							{periodText(applied.start_date, applied.end_date)} · {qtyUnit(details?.quantity, details?.unit_name)} · {money(details?.amount)} · {t('chain_incomes_avg_price')} {details?.avg_price !== null ? money(details?.avg_price) : '—'}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					{supplierRows.length > 1 && (
						<>
							<h6 className="mb-2">{t('chain_incomes_by_suppliers')}</h6>
							<table className="chain-table mb-3">
								<thead>
									<tr>
										<th>{t('supplier')}</th>
										<th className="text-end">{t('documents')}</th>
										<th className="text-end">{t('quantity')}</th>
										<th className="text-end">{t('amount')}</th>
										<th className="text-end">{t('chain_incomes_avg_price')}</th>
									</tr>
								</thead>
								<tbody>
									{supplierRows.map(item => (
										<tr key={item.supplier_id}>
											<td>{item.supplier_name}</td>
											<td className="chain-num">{formatMoney(item.documents)}</td>
											<td className="chain-num">{qtyUnit(item.quantity, details?.unit_name)}</td>
											<td className="chain-num">{money(item.amount)}</td>
											<td className="chain-num" style={heatStyle(item.price, supplierStats, false)}><b>{item.price !== null ? money(item.price) : '—'}</b></td>
										</tr>
									))}
								</tbody>
							</table>
						</>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('date')}</th>
								<th>{t('document_number')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('supplier')}</th>
								<th>{t('wms')}</th>
								<th className="text-end">{t('quantity')}</th>
								<th className="text-end">{t('price')}</th>
								<th className="text-end">{t('amount')}</th>
								<th className="text-end">{t('chain_incomes_document_total')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={10} />}
							{(details?.rows || []).map((row, index) => (
								<tr key={row.id}>
									<td className="chain-index">{index + 1}</td>
									<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
									<td>
										{row.document_number || `#${row.id}`}
										{row.note && <div className="chain-muted" style={{ maxWidth: '200px' }}>{row.note}</div>}
									</td>
									<td>{row.pos_name}</td>
									<td>{row.supplier_name}</td>
									<td>{row.wms_name || '—'}</td>
									<td className="chain-num">{qty(row.quantity)}</td>
									<td className="chain-num" style={heatStyle(row.price, documentStats, false)}><b>{row.price !== null ? money(row.price) : '—'}</b></td>
									<td className="chain-num">{money(row.amount)}</td>
									<td className="chain-num chain-muted">{money(row.total_amount)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Переплата филиалов относительно лучшей цены сети
function OverpayChart({ branches }) {
	const { t } = useTranslation()
	const rows = [...branches].filter(branch => branch.amount > 0).sort((a, b) => b.overpay - a.overpay)
	if (rows.length < 2) return null

	const series = [{ name: t('chain_incomes_overpay'), data: rows.map(branch => Math.round(branch.overpay)) }]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%', distributed: true } },
		colors: rows.map(branch => chainColor(branches.indexOf(branch))),
		dataLabels: { enabled: false },
		legend: { show: false },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(branch => branch.pos_name), labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: {
			y: {
				formatter: (value, { dataPointIndex }) => `${formatMoney(Math.round(value))} · ${percent(rows[dataPointIndex]?.overpay_percent, 2)} ${t('chain_incomes_of_purchases')}`,
			},
		},
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_incomes_overpay_by_branches')}</h5></div>
				<ReactApexChart type="bar" height={Math.max(240, rows.length * 38 + 80)} series={series} options={options} />
			</div>
		</div>
	)
}

export default ElementIncomes
